import i18next from 'i18next'
import React, { ReactNode, useId } from 'react'
import { Form, Spinner } from 'react-bootstrap'
import type { BsPrefixProps } from 'react-bootstrap/esm/helpers'
import { StrictOmit } from '../utils/strictOmit'
import type { FormControlProps } from './formControls'

export type FormLabelProps = {
  label?: ReactNode
  labelIsSrOnly?: boolean
}

export type FormErrorMessageProps = {
  invalid: boolean
  errorMessage?: string
  errorMessageId?: string
}

export type FormBooleanFieldsetItemProps = {
  label: string | ReactNode
  controlId: string
  checked: boolean
  onChange: (_: boolean) => void
  extraJSX?: ReactNode
}

export type PasswordScoreTypes = 'strong' | 'almost good' | 'weak' | 'none'

export type PasswordToggleTypes = {
  isChecked: boolean
  label: string
  title?: string
  onChange: (_: boolean) => void
}

export const FormUtils = {
  SubmittingSpinner: () => (
    <Spinner animation="border" role="status" as="span" size="sm">
      <span className="sr-text">{i18next.t<string>('form:Submitting')}</span>
    </Spinner>
  ),
  SubmittingSpinnerOrText: (props: { showLoader: boolean; text: string }) =>
    props.showLoader ? <FormUtils.SubmittingSpinner /> : props.text,
  OptionalLabel: (props: { required: boolean }) => {
    const label = i18next.exists('form:optional') ? i18next.t<string>('form:optional') : '(Optional)'
    return !props.required && <span>{` ${label}`}</span>
  },
  Label: (
    props: FormLabelProps & { required: boolean; htmlFor?: string; isLegend?: boolean; extraLabelClasses?: string }
  ) => (
    <Form.Label
      as={props.isLegend ? 'legend' : 'label'}
      className={props.labelIsSrOnly ? 'sr-text' : `fs__label ${props.extraLabelClasses || ''}`}
    >
      {props.label}
      <FormUtils.OptionalLabel {...props} />
    </Form.Label>
  ),
  Legend: (props: FormLabelProps & { required: boolean; className?: string }) => (
    <FormUtils.Label {...props} isLegend />
  ),
  ErrorText: (props: { errorMessageId: string; errorMessage: string }) => (
    <Form.Control.Feedback as="p" role="alert" type="invalid" id={props.errorMessageId} className="fs__error">
      {props.errorMessage}
    </Form.Control.Feedback>
  ),
  FieldText: (props: { fieldMessageId: string; fieldMessage: string }) => (
    <Form.Text as="p" id={props.fieldMessageId} className="fs__description">
      {props.fieldMessage}
    </Form.Text>
  ),
  ValidationTexts: (props: { fieldMessageId?: string; fieldMessage?: string } & FormErrorMessageProps) => (
    <>
      {props.invalid && props.errorMessage && props.errorMessageId && (
        <FormUtils.ErrorText errorMessageId={props.errorMessageId} errorMessage={props.errorMessage} />
      )}

      {props.fieldMessage && props.fieldMessageId && (
        <FormUtils.FieldText fieldMessageId={props.fieldMessageId} fieldMessage={props.fieldMessage} />
      )}
    </>
  ),
  Group: (props: {
    children: ReactNode
    groupAs?: BsPrefixProps['as']
    controlId?: string
    extraGroupClasses?: string
    onBlur?: React.FocusEventHandler<HTMLElement>
  }) => (
    <Form.Group
      as={props.groupAs}
      controlId={props.controlId}
      className={`fs__group ${props.extraGroupClasses || ''}`}
      onBlur={props.onBlur}
    >
      {props.children}
    </Form.Group>
  ),
  PasswordToggle: (props: FormControlProps) => {
    const { showPasswordToggle, disabled } = props
    if (!showPasswordToggle) return <></>
    const id = useId()
    const passwordToggleId = `a11y-${id}-form-password-toggle`
    return (
      <div className="fs__password-toggle">
        <input
          type="checkbox"
          id={passwordToggleId}
          checked={showPasswordToggle.isChecked}
          disabled={disabled}
          onChange={(e) => showPasswordToggle.onChange(e.currentTarget.checked)}
        />
        <label htmlFor={passwordToggleId} title={showPasswordToggle.title}>
          <span>{showPasswordToggle.label}</span>
        </label>
      </div>
    )
  },
  PasswordStrengthBar: (props: { password?: string; passwordScore: PasswordScoreTypes; errorMessageId: string }) => {
    const passwordStrengthText = (score: PasswordScoreTypes | 'short'): string => {
      switch (score) {
        case 'none':
          return ''
        case 'short':
          return 'Too short'
        case 'weak':
          return 'Weak'
        case 'almost good':
          return 'Almost good'
        case 'strong':
          return 'Strong!'
      }
    }

    const PasswordStrengthLayout = (score: PasswordScoreTypes | 'short') => (
      <div className={`fs__pw-strength pw-strength--${score.replace(/\s+/g, '-')}`}>
        <div className="fs__pw-strength-inner">
          <span className="fs__pw-strength-meter" />
        </div>
        <p id={props.errorMessageId} className="fs__pw-strength-feedback">
          {score !== 'none' && `Password strength: ${passwordStrengthText(score).toLowerCase()}`}
        </p>
      </div>
    )

    return props.password === undefined
      ? PasswordStrengthLayout('none')
      : props.password.length < 6
      ? PasswordStrengthLayout('short')
      : PasswordStrengthLayout(props.passwordScore)
  },
}
